import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Leaf, Download, Sparkles, FileText } from "lucide-react";
import { COMPANY, BROCHURE_URL, CATALOGUE_URL } from "@/lib/company";
import { openAndDownloadDossier } from "@/lib/download";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/quality", label: "Quality" },
  { to: "/certifications", label: "Certifications" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-brand-green flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-brand-charcoal text-lg tracking-tight">
                Aad Labs
              </div>
              <div className="text-[10px] uppercase tracking-widest text-brand-gold font-semibold">
                Global Export Trading
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" data-testid="nav-link-home" className={({ isActive }) => `px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "text-brand-green" : "text-brand-charcoal hover:text-brand-green"}`}>
              Home
            </NavLink>
            <NavLink to="/about" data-testid="nav-link-about" className={({ isActive }) => `px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "text-brand-green" : "text-brand-charcoal hover:text-brand-green"}`}>
              About Us
            </NavLink>

            {/* Products dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button
                data-testid="nav-products-toggle"
                className="px-4 py-2 text-sm font-semibold text-brand-charcoal hover:text-brand-green transition-colors inline-flex items-center gap-1"
              >
                Products <ChevronDown className="w-4 h-4" />
              </button>
              {productsOpen && (
                <div
                  data-testid="nav-products-dropdown"
                  className="absolute top-full left-0 min-w-[280px] bg-white border border-neutral-200 rounded-md shadow-xl py-2"
                >
                  <Link
                    to="/products/botanical-extracts"
                    data-testid="nav-link-botanical"
                    className="block px-5 py-3 text-sm text-brand-charcoal hover:bg-neutral-50 hover:text-brand-green transition-colors"
                  >
                    <div className="font-semibold">Botanical Extracts</div>
                    <div className="text-xs text-neutral-500 mt-0.5">Ashwagandha, Bacopa, Ginger, Turmeric</div>
                  </Link>
                  <Link
                    to="/products/spices-seeds-grains"
                    data-testid="nav-link-spices"
                    className="block px-5 py-3 text-sm text-brand-charcoal hover:bg-neutral-50 hover:text-brand-green transition-colors"
                  >
                    <div className="font-semibold">Spices, Seeds & Grains</div>
                    <div className="text-xs text-neutral-500 mt-0.5">Pepper, Cumin, Turmeric, Rice & more</div>
                  </Link>
                  <Link
                    to="/products/coffee-beans"
                    data-testid="nav-link-coffee"
                    className="block px-5 py-3 text-sm text-brand-charcoal hover:bg-neutral-50 hover:text-brand-green transition-colors"
                  >
                    <div className="font-semibold">Coffee Beans</div>
                    <div className="text-xs text-neutral-500 mt-0.5">Arabica & Robusta — shade-grown</div>
                  </Link>
                  <Link
                    to="/products/eco-packaging"
                    data-testid="nav-link-packaging"
                    className="block px-5 py-3 text-sm text-brand-charcoal hover:bg-neutral-50 hover:text-brand-green transition-colors"
                  >
                    <div className="font-semibold">Eco Packaging</div>
                    <div className="text-xs text-neutral-500 mt-0.5">Compostable cups, lids & straws</div>
                  </Link>
                  <Link
                    to="/products/essential-oils"
                    data-testid="nav-link-essential-oils"
                    className="block px-5 py-3 text-sm text-brand-charcoal hover:bg-neutral-50 hover:text-brand-green transition-colors"
                  >
                    <div className="font-semibold inline-flex items-center gap-2">
                      Essential Oils
                      <span className="text-[9px] tracking-widest uppercase text-brand-gold border border-brand-gold/50 rounded-full px-1.5 py-0.5">New</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">Steam-distilled aromatic oils & absolutes</div>
                  </Link>
                  <div className="mx-3 my-1 h-px bg-neutral-200" />
                  <a
                    href={CATALOGUE_URL}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={openAndDownloadDossier}
                    data-testid="nav-link-catalogue"
                    className="block px-5 py-3 text-sm text-brand-charcoal hover:bg-neutral-50 hover:text-brand-green transition-colors"
                  >
                    <div className="font-semibold inline-flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-brand-green" />
                      Download Product Catalogue
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">16-page spec sheet · MOQ · lead time</div>
                  </a>
                  <Link
                    to="/products/eco-packaging/experience"
                    data-testid="nav-link-experience"
                    className="block px-5 py-3 text-sm text-brand-charcoal hover:bg-[#152018] hover:text-brand-gold transition-colors group"
                  >
                    <div className="font-semibold inline-flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-brand-gold" />
                      The Ascension
                      <span className="text-[9px] tracking-widest uppercase text-brand-gold border border-brand-gold/50 rounded-full px-1.5 py-0.5">New</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 group-hover:text-white/70">Immersive scroll experience</div>
                  </Link>
                </div>
              )}
            </div>

            <NavLink to="/quality" data-testid="nav-link-quality" className={({ isActive }) => `px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "text-brand-green" : "text-brand-charcoal hover:text-brand-green"}`}>
              Quality
            </NavLink>
            <NavLink to="/certifications" data-testid="nav-link-certifications" className={({ isActive }) => `px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "text-brand-green" : "text-brand-charcoal hover:text-brand-green"}`}>
              Certifications
            </NavLink>
            <NavLink to="/contact" data-testid="nav-link-contact" className={({ isActive }) => `px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "text-brand-green" : "text-brand-charcoal hover:text-brand-green"}`}>
              Contact
            </NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/products/eco-packaging/experience"
              data-testid="nav-experience-chip"
              className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-gold/50 text-brand-charcoal text-sm font-semibold hover:bg-[#152018] hover:text-brand-gold hover:border-brand-gold transition-all"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-gold opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-gold" />
              </span>
              <Sparkles className="w-3.5 h-3.5 text-brand-gold" />
              Experience
            </Link>
            <a
              href={BROCHURE_URL}
              download="Aad-Labs-Export-Dossier.pdf"
              target="_blank"
              rel="noopener noreferrer"
              onClick={openAndDownloadDossier}
              data-testid="nav-download-brochure"
              className="btn-green text-sm"
              style={{ padding: "0.65rem 1.1rem" }}
            >
              <Download className="w-4 h-4" />
              Company Profile
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-brand-charcoal"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-neutral-200">
          <div className="px-4 py-4 space-y-1">
            {navLinks.slice(0, 2).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block px-3 py-3 text-brand-charcoal font-semibold hover:bg-neutral-50 rounded"
              >
                {l.label}
              </Link>
            ))}
            <div className="px-3 py-2 text-xs uppercase tracking-widest text-brand-gold font-bold">Products</div>
            <Link to="/products/botanical-extracts" data-testid="mobile-nav-botanical" className="block px-3 py-3 text-brand-charcoal hover:bg-neutral-50 rounded">
              Botanical Extracts
            </Link>
            <Link to="/products/spices-seeds-grains" data-testid="mobile-nav-spices" className="block px-3 py-3 text-brand-charcoal hover:bg-neutral-50 rounded">
              Spices, Seeds & Grains
            </Link>
            <Link to="/products/coffee-beans" data-testid="mobile-nav-coffee" className="block px-3 py-3 text-brand-charcoal hover:bg-neutral-50 rounded">
              Coffee Beans
            </Link>
            <Link to="/products/eco-packaging" data-testid="mobile-nav-packaging" className="block px-3 py-3 text-brand-charcoal hover:bg-neutral-50 rounded">
              Eco Packaging
            </Link>
            <Link to="/products/essential-oils" data-testid="mobile-nav-essential-oils" className="flex items-center gap-2 px-3 py-3 text-brand-charcoal hover:bg-neutral-50 rounded">
              Essential Oils
              <span className="ml-auto text-[9px] tracking-widest uppercase text-brand-gold border border-brand-gold/50 rounded-full px-1.5 py-0.5">New</span>
            </Link>
            <Link
              to="/products/eco-packaging/experience"
              data-testid="mobile-nav-experience"
              className="flex items-center gap-2 px-3 py-3 text-brand-gold font-semibold bg-[#152018]/5 hover:bg-[#152018] hover:text-brand-gold rounded"
            >
              <Sparkles className="w-4 h-4" />
              The Ascension · Experience
              <span className="ml-auto text-[9px] tracking-widest uppercase border border-brand-gold/50 rounded-full px-1.5 py-0.5">New</span>
            </Link>
            <a
              href={CATALOGUE_URL}
              download
              target="_blank"
              rel="noopener noreferrer"
              onClick={openAndDownloadDossier}
              data-testid="mobile-nav-catalogue"
              className="flex items-center gap-2 px-3 py-3 text-brand-green font-semibold hover:bg-neutral-50 rounded"
            >
              <FileText className="w-4 h-4" />
              Product Catalogue (PDF)
            </a>
            {navLinks.slice(2).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block px-3 py-3 text-brand-charcoal font-semibold hover:bg-neutral-50 rounded"
              >
                {l.label}
              </Link>
            ))}
            <Link to="/contact" data-testid="mobile-nav-cta" className="btn-green w-full justify-center mt-2">
              Request Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
